import React, { useState } from 'react';
import { Droplet } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const MGDL_PER_MMOL = 18.0182;

const classify = (mgdl, timing, t) => {
  if (timing === 'jeun') {
    if (mgdl < 70) return { label: t('glycemia_cat_low'), color: '#EF4444' };
    if (mgdl <= 100) return { label: t('glycemia_cat_normal'), color: '#10B981' };
    if (mgdl <= 125) return { label: t('glycemia_cat_prediabetes_fasting'), color: '#F59E0B' };
    return { label: t('glycemia_cat_high'), color: '#EF4444' };
  }
  // post-prandiale (2h après repas)
  if (mgdl < 70) return { label: t('glycemia_cat_low'), color: '#EF4444' };
  if (mgdl < 140) return { label: t('glycemia_cat_normal'), color: '#10B981' };
  if (mgdl < 200) return { label: t('glycemia_cat_prediabetes_postprandial'), color: '#F59E0B' };
  return { label: t('glycemia_cat_high'), color: '#EF4444' };
};

const GlycemiaConverter = () => {
  const { t } = useLanguage();
  const [mgdl, setMgdl] = useState('');
  const [mmol, setMmol] = useState('');
  const [timing, setTiming] = useState('jeun');
  const [hba1c, setHba1c] = useState('');

  const updateFromMgdl = (val) => {
    setMgdl(val);
    const n = parseFloat(val);
    setMmol(n > 0 ? (n / MGDL_PER_MMOL).toFixed(2) : '');
  };

  const updateFromMmol = (val) => {
    setMmol(val);
    const n = parseFloat(val);
    setMgdl(n > 0 ? Math.round(n * MGDL_PER_MMOL) : '');
  };

  const mgdlNum = parseFloat(mgdl);
  const cat = mgdlNum > 0 ? classify(mgdlNum, timing, t) : null;

  const hba1cNum = parseFloat(hba1c);
  const eag = hba1cNum > 0 ? Math.round(28.7 * hba1cNum - 46.7) : null;

  return (
    <ToolPageLayout
      title={t('glycemia_title')}
      subtitle={t('glycemia_subtitle')}
      specialty="endocrinologue"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Droplet className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('glycemia_info')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-2">
        <div>
          <Label className="mb-1 block">{t('glycemia_mgdl_label')}</Label>
          <Input
            type="number"
            min="0"
            value={mgdl}
            onChange={(e) => updateFromMgdl(e.target.value)}
            placeholder="Ex: 110"
            className="rounded-xl"
          />
        </div>
        <div>
          <Label className="mb-1 block">{t('glycemia_mmol_label')}</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={mmol}
            onChange={(e) => updateFromMmol(e.target.value)}
            placeholder="Ex: 6.1"
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="mb-6">
        <Label className="mb-1 block">{t('glycemia_timing_label')}</Label>
        <select
          value={timing}
          onChange={(e) => setTiming(e.target.value)}
          className="w-full sm:w-auto border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        >
          <option value="jeun">{t('glycemia_timing_fasting')}</option>
          <option value="postprandial">{t('glycemia_timing_postprandial')}</option>
        </select>
      </div>

      {cat && (
        <div
          className="rounded-2xl p-6 text-center border mb-8"
          style={{ borderColor: `${cat.color}40`, backgroundColor: `${cat.color}0D` }}
        >
          <div className="text-sm text-[#64748B] mb-1">{t('glycemia_result_label')}</div>
          <div className="text-xl font-bold" style={{ color: cat.color }}>{cat.label}</div>
        </div>
      )}

      <div className="border-t border-[#E2E8F0] pt-6">
        <h3 className="font-semibold text-[#0F172A] mb-3">{t('glycemia_hba1c_heading')}</h3>
        <div className="grid sm:grid-cols-2 gap-5 items-end">
          <div>
            <Label className="mb-1 block">{t('glycemia_hba1c_label')}</Label>
            <Input
              type="number"
              min="0"
              step="0.1"
              value={hba1c}
              onChange={(e) => setHba1c(e.target.value)}
              placeholder="Ex: 6.5"
              className="rounded-xl"
            />
          </div>
          {eag !== null && (
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
              <p className="text-sm text-[#64748B]">{t('glycemia_eag_label')}</p>
              <p className="text-lg font-bold text-[#0596DE]">
                {eag} mg/dL ({(eag / MGDL_PER_MMOL).toFixed(1)} mmol/L)
              </p>
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-sm text-[#64748B]">
        {t('glycemia_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default GlycemiaConverter;
